<template>
    <div class="rm-table-container">
        <!-- Desktop View -->
        <div v-if="!isMobile" class="desktop-view">
            <CommonTableComponent ref="dataTable" :tableData="filteredTableData" :columns="columns"
                :totalRecords="totalRecords" :rows="pageSize" :first="first" :rowLoading="loading" :dataFetched="!loading"
                :isSelectionEnabled="false" :isExpanderEnabled="false" :isExportCSV="false" fileName="RM_Management_Data"
                tableHeader="RM Management" :loadingMessage="loadingMessage" :isMenuVisible="showMenuActions"
                :get-actions="getActions" :filters="localFilters" defaultSortField="id" :defaultSortOrder="1"
                :showToggleAction="false" :showDeleteAction="false" @page-change="onPageChange"
                @update-with-filter="filterUpdate" />
        </div>

        <!-- Mobile View -->
        <div v-else class="mobile-view">
            <RMMobileCardView 
                ref="mobileCardView"
                :tableData="filteredTableData" 
                :defaultColumns="columns"
                :totalRecords="totalRecords" 
                :rows="pageSize" 
                :first="first"
                :loading="loading"
                :loadingMessage="loadingMessage"
                @page-change="onPageChange"
                @inactive-action="handleInactiveAction" />
        </div>
    </div>
</template>
<script>
import CommonTableComponent from '@/components/CommonTableComponent.vue';
import RMMobileCardView from './RMMobileCardView.vue';
import { FilterMatchMode } from '@primevue/core/api';
import { getDistributer } from '@/mixins/common';

export default {
    name: 'RMTable',
    components: {
        CommonTableComponent,
        RMMobileCardView
    },
    props: {
        filters: {
            type: Object,
            default: () => ({})
        },
        columns: {
            type: Array,
            required: true
        },
        allColumns: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            totalRecords: 0,
            pageSize: 20,
            pageNumber: 0,
            displayPage: 1,
            first: 0,
            loading: false,
            loadingMessage: 'Loading RM Management Data. Please wait.',
            localFilters: {},
            allTableData: [],
            displayColumns: this.columns?.length ? [...this.columns] : [...this.allColumns],
            currentFilter: '',
            distributorID: "",
            isMobile: false, // Track mobile/desktop view
        }
    },
    computed: {
        filteredTableData() {
            if (!this.localFilters?.global?.value) {
                return this.allTableData;
            }

            const searchValue = String(this.localFilters.global.value).toLowerCase();
            return this.allTableData.filter(obj =>
                Object.values(obj).some(val =>
                    String(val).toLowerCase().includes(searchValue)
                )
            );
        },
        filteredTotalRecords() {
            return this.filteredTableData.length;
        }
    },
    watch: {
        filters: {
            handler(newVal) {
                if (newVal && newVal.global) {
                    this.localFilters.global = { ...this.localFilters.global, ...newVal.global };
                }
            },
            deep: true,
            immediate: true
        },
        columns: {
            handler(newVal) {
                if (newVal?.length) {
                    this.displayColumns = [...newVal];
                } else if (this.allColumns?.length) {
                    this.displayColumns = [...this.allColumns];
                }
            },
            deep: true,
            immediate: true
        },
        allColumns(newVal) {
            if (!this.columns?.length && newVal?.length) {
                this.displayColumns = [...newVal];
            }
        },
        'localFilters.global.value'() {
            // Trigger reactivity when global filter changes
        }
    },
    mounted() {
        const distributer = getDistributer();
        if (distributer && distributer["distributerID"]) {
            this.distributorID = distributer["distributerID"] || "";
        }
        this.initFilters();
        this.loadFilterFromUrl();
        this.getRmData(this.currentFilter);
        // Detect mobile view
        this.checkMobileView();
        // Listen for window resize
        window.addEventListener('resize', this.checkMobileView);
    },
    beforeUnmount() {
        // Clean up event listener
        window.removeEventListener('resize', this.checkMobileView);
    },
    methods: {
        async getRmData(filter = null) {
            this.loading = true;
            this.pageNumber = this.displayPage > 0 ? this.displayPage - 1 : 0;

            const filterValue = filter !== null ? filter : (this.currentFilter || '');

            const queryParams = {
                pageSize: this.pageSize ? this.pageSize : 20,
                pageNumber: this.pageNumber,
                spCode: '',
                startDate: '',
                endDate: '',
                modifiedDate: '',
                operatorId: '',
                columnName: '',
                filterText: '',
                filter: filterValue
            };

            this.loadingMessage = 'Fetching RM Management Data.';

            try {
                const param = {
                    pageSize: queryParams.pageSize,
                    pageNumber: queryParams.pageNumber,
                    spCode: queryParams.spCode,
                    startDate: queryParams.startDate,
                    endDate: queryParams.endDate,
                    modifiedDate: queryParams.modifiedDate,
                    operatorId: queryParams.operatorId,
                    columnName: queryParams.columnName,
                    filterText: queryParams.filterText,
                    filter: queryParams.filter
                };
                const response = await this.backendApi("getRmData", {}, {}, "get", param);

                this.allTableData = [];

                this.loading = false;

                if (response && response.data && response.data.data && response.data.data.resp) {
                    let data = response.data.data.resp;
                    this.allTableData = data.sort((a, b) => {
                        const idA = a.id || 0;
                        const idB = b.id || 0;
                        return idA - idB;
                    });
                    this.totalRecords = response.data.data.count;
                } else if (response && response.data && response.data.data && response.data.data.errors) {
                    if (this.$toast) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: response?.data?.errors?.[0]?.errorDisplayMessage || 'Failed to fetch RM data',
                            life: 3000
                        });
                    }
                    this.allTableData = [];
                    this.totalRecords = 0;
                } else {
                    this.allTableData = [];
                    this.totalRecords = 0;
                }

                if (this.totalRecords == 0) {
                    this.displayPage = 0;
                    this.first = 0;
                } else {
                    this.first = this.pageNumber * this.pageSize;
                }
            } catch (error) {
                this.loading = false;
                console.error('An error occurred while fetching RM data:', error);
                this.allTableData = [];
                this.totalRecords = 0;
            }
        },
        onPageChange(event) {
            this.pageNumber = event.page;
            this.pageSize = event.rows;
            this.displayPage = event.page + 1;
            this.first = event.first;
            this.getRmData(this.currentFilter);
        },
        initFilters() {
            this.localFilters = {
                global: this.filters?.global ? { ...this.filters.global } : { value: null, matchMode: FilterMatchMode.CONTAINS },
                id: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
                rmCode: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
                manufacturerId: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
                spCode: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
                operatorId: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
                operatorReference: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
                active: { constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            };
        },
        filterUpdate(filter) {
            if (filter === '') {
                this.initFilters();
                this.currentFilter = '';
                this.updateUrlParams('');
            } else {
                this.currentFilter = filter;
                this.updateUrlParams(filter);
            }
            this.displayPage = 1;
            this.first = 0;
            this.getRmData(filter);
            this.$emit('update-with-filter', filter);
        },
        loadFilterFromUrl() {
            const filterParam = this.$route?.query?.filter;
            if (filterParam) {
                this.currentFilter = filterParam;
                if (this.localFilters?.global) {
                    this.localFilters.global.value = filterParam;
                }
            }
        },
        updateUrlParams(filter) {
            const query = { ...this.$route.query };
            if (filter && filter !== '') {
                query.filter = filter;
            } else {
                delete query.filter;
            }
            this.$router.replace({ query }).catch(() => { });
        },
        showMenuActions(data) {
            // Show menu actions for all rows
            return true;
        },
        getActions(rowData) {
            const actions = [];
            // Extract the actual row data - PrimeVue DataTable passes rowData with nested structure
            const actualRowData = rowData.data || rowData;
            
            // Add Inactive/Active option
            actions.push({
                label: 'Inactive',
                icon: 'pi pi-ban',
                command: () => {
                    this.handleInactiveAction(actualRowData);
                }
            });
            
            return actions;
        },
        exportCSV() {
            if (this.$refs.dataTable) {
                this.$refs.dataTable.exportCSV();
            }
        },
        setColumns(value) {
            this.columns = value?.length ? value : this.columns;
        },
        handleInactiveAction(rowData) {
            this.$confirm.require({
                message: 'Are you sure, this can\'t be undone.',
                header: 'Inactive Confirmation',
                icon: 'pi pi-exclamation-triangle',
                rejectClass: 'p-button-secondary p-button-outlined',
                rejectLabel: 'No',
                acceptLabel: 'Yes',
                accept: async () => {
                    await this.toggleRecord(rowData);
                }
            });
        },
        async toggleRecord(rowData) {
            try {
                const currentActiveState = rowData.active === 1 || rowData.active === true;
                const newActiveState = currentActiveState ? 0 : 1;

                const updatedRowData = {
                    ...rowData,
                    active: newActiveState
                };

                const payload = { data: [updatedRowData] };
                const headers = {
                    'x-secret-key': '1SB'
                };
                const param = { id: updatedRowData.id };
                const response = await this.backendApi("updateRm", payload, headers, "put", param);

                if (response && response.data && response.data.data) {
                    this.allTableData = [];
                    await this.getRmData(this.currentFilter);

                    if (this.$toast) {
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: `Record ${newActiveState === 1 ? 'activated' : 'deactivated'} successfully`,
                            life: 3000
                        });
                    }
                } else if (response && response.data && response.data.errors && response.data.errors.length > 0) {
                    if (this.$toast) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: response?.data?.errors?.[0]?.errorDisplayMessage || 'Failed to update record',
                            life: 3000
                        });
                    }
                }
            } catch (error) {
                console.error('Error toggling active status:', error);
                if (this.$toast) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to update record',
                        life: 3000
                    });
                }
            }
        },
        handleDeleteAction(rowData) {
            this.$confirm.require({
                message: 'Are you sure, this can\'t be undone.',
                header: 'Delete Confirmation',
                icon: 'pi pi-exclamation-triangle',
                rejectClass: 'p-button-secondary p-button-outlined',
                rejectLabel: 'No',
                acceptLabel: 'Yes',
                accept: async () => {
                    await this.deleteRecord(rowData.id);
                }
            });
        },
        async deleteRecord(id) {
            try {
                // Validate ID
                if (!id) {
                    throw new Error('Record ID is required');
                }

                const headers = {
                    'x-secret-key': '1SB'
                };
                const param = { id: id };
                const response = await this.backendApi("deleteRm", {}, headers, "delete", param);

                if (response && response.data && response.data.data && response.data.data.status.toLowerCase() == 'success') {
                    await this.getRmData(this.currentFilter);

                    if (this.$toast) {
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Record deleted successfully',
                            life: 3000
                        });
                    }
                } else {
                    throw new Error(response?.data?.errors?.[0]?.errorDisplayMessage || 'Failed to delete record');
                }
            } catch (error) {
                console.error('Error deleting record:', error);
                if (this.$toast) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to delete record',
                        life: 3000
                    });
                }
            }
        },
        checkMobileView() {
            // Check if window width is less than 768px (typical mobile breakpoint)
            this.isMobile = window.innerWidth < 768;
        }
    }
}
</script>
<style scoped lang="scss">
@import '@/assets/scss/distributorVariables/variable.scss';

.rm-table-container {
    width: 100%;
}

.desktop-view {
    width: 100%;
}

.mobile-view {
    width: 100%;
}

// Hide desktop view on mobile
@media (max-width: 767px) {
    .desktop-view {
        display: none;
    }
}

// Hide mobile view on desktop
@media (min-width: 768px) {
    .mobile-view {
        display: none;
    }
}
</style>

